import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  Eye, 
  Target, 
  TrendingUp, 
  Lightbulb,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Zap
} from 'lucide-react';
import { ShotComparisonData } from '../types';
import { useShotVisualizationStore } from '../stores/shotVisualizationStore';

interface ShotComparisonProps {
  shotComparison: ShotComparisonData;
  className?: string;
}

export const ShotComparison: React.FC<ShotComparisonProps> = ({
  shotComparison,
  className = ''
}) => {
  const { actual, optimal, optimization } = shotComparison;
  const { 
    animation, 
    config, 
    playAnimation, 
    pauseAnimation, 
    resetAnimation, 
    updateConfig 
  } = useShotVisualizationStore();

  const [activePhase, setActivePhase] = useState<'overview' | 'technique' | 'recommendations'>('overview');

  // Helper functions
  const formatNumber = (value: number, decimals: number = 1) => value.toFixed(decimals);

  const getQualityBadgeColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-yellow-100 text-yellow-800';
      case 'fair': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Animation Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Shot Analysis: Actual vs Optimal
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={animation.isPlaying ? pauseAnimation : playAnimation}
                className="flex items-center gap-2"
              >
                {animation.isPlaying ? (
                  <>
                    <Pause className="h-4 w-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Play
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={resetAnimation}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Animation Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Animation Progress</span>
              <span>
                {(animation.currentTime / 1000).toFixed(1)}s / {(animation.totalDuration / 1000).toFixed(1)}s
              </span>
            </div>
            <Progress 
              value={(animation.currentTime / animation.totalDuration) * 100} 
              className="h-2"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Pre-Impact</span>
              <span>Impact</span>
              <span>Post-Impact</span>
            </div>
          </div>

          {/* Visualization Controls */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant={config.showActualTrajectory ? "default" : "outline"}
              size="sm"
              onClick={() => updateConfig({ showActualTrajectory: !config.showActualTrajectory })}
            >
              Actual Path
            </Button>
            <Button
              variant={config.showOptimalTrajectory ? "default" : "outline"}
              size="sm"
              onClick={() => updateConfig({ showOptimalTrajectory: !config.showOptimalTrajectory })}
            >
              Optimal Path
            </Button>
            <Button
              variant={config.showMetrics ? "default" : "outline"}
              size="sm"
              onClick={() => updateConfig({ showMetrics: !config.showMetrics })}
            >
              Annotations
            </Button>
            <Button
              variant={config.showBall ? "default" : "outline"}
              size="sm"
              onClick={() => updateConfig({ showBall: !config.showBall })}
            >
              Ball
            </Button>
            <Button
              variant={config.showBat ? "default" : "outline"}
              size="sm"
              onClick={() => updateConfig({ showBat: !config.showBat })}
            >
              Bat
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Comparison */}
      <Tabs value={activePhase} onValueChange={(value) => setActivePhase(value as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="technique">Technique</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-center space-y-2">
                  <Target className="h-6 w-6 mx-auto text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(actual.distance)}m</div>
                    <div className="text-sm text-gray-600">Distance</div>
                    <div className="text-xs text-green-600">
                      Optimal: {formatNumber(optimal.distance)}m
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-center space-y-2">
                  <Zap className="h-6 w-6 mx-auto text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(actual.exitVelocity)}</div>
                    <div className="text-sm text-gray-600">Exit Velocity (m/s)</div>
                    <div className="text-xs text-green-600">
                      Optimal: {formatNumber(optimal.exitVelocity)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-center space-y-2">
                  <TrendingUp className="h-6 w-6 mx-auto text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(actual.launchAngle)}°</div>
                    <div className="text-sm text-gray-600">Launch Angle</div>
                    <div className="text-xs text-green-600">
                      Optimal: {formatNumber(optimal.launchAngle)}°
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-center space-y-2">
                  <CheckCircle className="h-6 w-6 mx-auto text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(actual.powerTransfer * 100)}%</div>
                    <div className="text-sm text-gray-600">Power Transfer</div>
                    <div className="text-xs text-gray-500">
                      Sweet Spot: {actual.sweetSpotDistance < 0.02 ? 'Hit' : 'Missed'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side-by-Side Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Actual Shot */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Actual Shot</span>
                  <Badge className={getQualityBadgeColor(actual.quality)} variant="secondary">
                    {actual.quality.toUpperCase()}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Ball Speed</p>
                    <p className="font-medium">{formatNumber(actual.ballSpeed)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Bat Speed</p>
                    <p className="font-medium">{formatNumber(actual.batSpeed)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Exit Velocity</p>
                    <p className="font-medium">{formatNumber(actual.exitVelocity)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Distance</p>
                    <p className="font-medium">{formatNumber(actual.distance)} m</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Launch Angle</p>
                    <p className="font-medium">{formatNumber(actual.launchAngle)}°</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Horizontal Angle</p>
                    <p className="font-medium">{formatNumber(actual.horizontalAngle)}°</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Spin Rate</p>
                    <p className="font-medium">{formatNumber(actual.spinRate, 0)} RPM</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Max Height</p>
                    <p className="font-medium">{formatNumber(actual.height)} m</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Optimal Shot */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Optimal Shot</span>
                  <Badge className="bg-green-100 text-green-800" variant="secondary">
                    PERFECT
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Ball Speed</p>
                    <p className="font-medium">{formatNumber(optimal.ballSpeed)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Bat Speed</p>
                    <p className="font-medium">{formatNumber(optimal.batSpeed)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Exit Velocity</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.exitVelocity)} m/s</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Distance</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.distance)} m</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Launch Angle</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.launchAngle)}°</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Horizontal Angle</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.horizontalAngle)}°</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Spin Rate</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.spinRate, 0)} RPM</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Max Height</p>
                    <p className="font-medium text-green-600">{formatNumber(optimal.height)} m</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Improvement Potential */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Improvement Potential
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg">
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-green-600">
                    +{formatNumber(optimization.potentialDistanceGain)} m
                  </div>
                  <p className="text-gray-600">Potential Distance Gain</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-medium">Launch Angle</p>
                    <p className={`text-lg ${optimization.improvements.launchAngleDelta >= 0 ? 'text-green-600' : 'text-blue-600'}`}>
                      {optimization.improvements.launchAngleDelta >= 0 ? '+' : ''}
                      {formatNumber(optimization.improvements.launchAngleDelta)}°
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-medium">Exit Velocity</p>
                    <p className={`text-lg ${optimization.improvements.velocityDelta >= 0 ? 'text-green-600' : 'text-blue-600'}`}>
                      {optimization.improvements.velocityDelta >= 0 ? '+' : ''}
                      {formatNumber(optimization.improvements.velocityDelta)} m/s
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-medium">Spin Rate</p>
                    <p className={`text-lg ${optimization.improvements.spinRateDelta >= 0 ? 'text-green-600' : 'text-blue-600'}`}>
                      {optimization.improvements.spinRateDelta >= 0 ? '+' : ''}
                      {formatNumber(optimization.improvements.spinRateDelta, 0)} RPM
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technique Tab */}
        <TabsContent value="technique" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Technique Assessment */}
            <Card>
              <CardHeader>
                <CardTitle>Technique Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: 'Timing', value: actual.timing },
                  { label: 'Footwork', value: actual.footwork },
                  { label: 'Balance', value: actual.balance },
                  { label: 'Follow Through', value: actual.followThrough }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <Badge className={getQualityBadgeColor(item.value)} variant="secondary">
                        {item.value.toUpperCase()}
                      </Badge>
                      {item.value === 'excellent' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-orange-600" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Impact Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Impact Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Sweet Spot Distance</span>
                    <span className={`font-medium ${
                      actual.sweetSpotDistance < 0.02 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(actual.sweetSpotDistance * 100).toFixed(1)} cm
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span>Power Transfer</span>
                    <span className={`font-medium ${
                      actual.powerTransfer > 0.8 ? 'text-green-600' : 
                      actual.powerTransfer > 0.6 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {(actual.powerTransfer * 100).toFixed(1)}%
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span>Impact Location X</span>
                    <span className="font-mono">{(actual.impactLocationX * 100).toFixed(1)} cm</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span>Impact Location Y</span>
                    <span className="font-mono">{(actual.impactLocationY * 100).toFixed(1)} cm</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium mb-1">Contact Quality</p>
                  <p className="text-xs text-gray-600">
                    {actual.sweetSpotDistance < 0.02 
                      ? "Excellent sweet spot contact achieved!"
                      : "Impact missed sweet spot - focus on centering contact"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Technical Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  Technical Improvements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {optimization.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-900">{recommendation}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Practice Drills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Practice Drills
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {optimization.drillSuggestions.map((drill, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-green-900">{drill}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Priority Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Priority Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-2">Focus Areas for Next Practice</h4>
                <div className="space-y-2">
                  {Math.abs(optimization.improvements.launchAngleDelta) > 3 && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4" />
                      <span>Launch angle adjustment ({optimization.improvements.launchAngleDelta > 0 ? 'increase' : 'decrease'} by {Math.abs(optimization.improvements.launchAngleDelta).toFixed(1)}°)</span>
                    </div>
                  )}
                  {optimization.improvements.velocityDelta > 2 && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4" />
                      <span>Bat speed improvement needed (+{optimization.improvements.velocityDelta.toFixed(1)} m/s)</span>
                    </div>
                  )}
                  {actual.sweetSpotDistance > 0.02 && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4" />
                      <span>Sweet spot contact consistency</span>
                    </div>
                  )}
                  {actual.powerTransfer < 0.8 && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4" />
                      <span>Power transfer efficiency (timing and contact)</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};